import React, { useEffect, useRef } from "react";
import { trackSectionView, trackSectionDwell } from "../lib/analytics";

// Wraps each portal section, registers it with the scroll-spy, and fires a
// one-time engagement event when it first enters the viewport.
export function SectionShell({ id, eyebrow, headline, children, onActive, registerRef }) {
  const ref = useRef(null);
  const viewed = useRef(false);
  const enteredAt = useRef(null);
  const totalDwell = useRef(0);

  useEffect(() => {
    const el = ref.current;
    if (registerRef) registerRef(id, el);
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          const inView = e.isIntersecting && e.intersectionRatio >= 0.3;
          if (inView) {
            if (e.intersectionRatio >= 0.4 && onActive) onActive(id);
            if (!viewed.current) {
              viewed.current = true;
              trackSectionView(id);
            }
            if (enteredAt.current == null) enteredAt.current = Date.now();
          } else if (enteredAt.current != null) {
            totalDwell.current += Date.now() - enteredAt.current;
            enteredAt.current = null;
          }
        });
      },
      { threshold: [0.15, 0.3, 0.4] }
    );
    obs.observe(el);

    const flush = () => {
      if (enteredAt.current != null) {
        totalDwell.current += Date.now() - enteredAt.current;
        enteredAt.current = null;
      }
      if (totalDwell.current > 0) {
        trackSectionDwell(id, totalDwell.current);
        totalDwell.current = 0;
      }
    };
    const onHide = () => { if (document.visibilityState === "hidden") flush(); };
    window.addEventListener("beforeunload", flush);
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onHide);
    // Also flush every 30s while the tab is open, so long dwells aren't lost
    const t = setInterval(() => {
      if (enteredAt.current != null) {
        totalDwell.current += Date.now() - enteredAt.current;
        enteredAt.current = Date.now();
      }
      if (totalDwell.current >= 3000) {
        trackSectionDwell(id, totalDwell.current);
        totalDwell.current = 0;
      }
    }, 30000);
    return () => {
      obs.disconnect();
      window.removeEventListener("beforeunload", flush);
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onHide);
      clearInterval(t);
      flush();
    };
  }, [id, onActive, registerRef]);

  return (
    <section
      id={id}
      ref={ref}
      data-testid={`section-${id}`}
      className="scroll-mt-4 border-b border-line/70 px-6 py-5 md:px-14 md:py-7 lg:px-20 lg:py-8"
    >
      <div className="max-w-5xl">
        {headline && (
          <h2 className="font-heading font-black tracking-tight text-3xl md:text-4xl lg:text-5xl leading-[1.05] text-ftext mb-4">
            {headline}
          </h2>
        )}
        {children}
      </div>
    </section>
  );
}
